import { useEffect, useState } from 'preact/hooks';
import { supabase } from '../lib/supabase';

/** Laptop layout from 700 px wide (iPads both ways); phone layout below (Kristi, Oct 26). */
export const WIDE_QUERY = '(min-width: 700px)';

export function useWide(): boolean {
  return useMatches(WIDE_QUERY);
}

/** Does a media query match now (and when the window changes)? */
export function useMatches(query: string): boolean {
  const mq = typeof window !== 'undefined' ? window.matchMedia(query) : null;
  const [wide, setWide] = useState(mq ? mq.matches : true);
  useEffect(() => {
    if (!mq) return;
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return wide;
}

/** Is a feature flag on for the signed-in person? (Reads only; null while asking.) */
// Each flag's answer, kept for this page load, so coming back to a section doesn't ask again.
const flagSeen = new Map<string, boolean>();

export function useFlag(flag: string): boolean | null {
  const [on, setOn] = useState<boolean | null>(flagSeen.get(flag) ?? null);
  useEffect(() => {
    let live = true;
    if (!supabase) {
      setOn(false);
      return;
    }
    supabase.rpc('flag_on', { flag }).then(({ data, error }) => {
      const v = !error && data === true;
      flagSeen.set(flag, v);
      if (live) setOn(v);
    });
    return () => {
      live = false;
    };
  }, [flag]);
  return on;
}

/**
 * "Updated just now": when the page last had fresh data. For now that's when it loaded,
 * or when you came back to the tab (Phase 6 refreshes the data at that moment too).
 */
export function useUpdated(): string {
  const [at, setAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const back = () => {
      if (document.visibilityState === 'visible') {
        setAt(Date.now());
        setNow(Date.now());
      }
    };
    document.addEventListener('visibilitychange', back);
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => {
      document.removeEventListener('visibilitychange', back);
      clearInterval(t);
    };
  }, []);
  return updatedWords(now - at);
}

export function updatedWords(ms: number): string {
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'Updated just now';
  if (min < 60) return `Updated ${min} min ago`;
  const h = Math.floor(min / 60);
  return `Updated ${h} h ago`;
}
